/**
 * Hook pour la génération PDF et la soumission signée de la feuille d'heures.
 *
 * La construction du PDF est déléguée à buildTimesheetPDF (utils/timesheetPDFBuilder.ts).
 * Ce hook ne gère que : capture signature, chargement images, upload Storage, saveToDatabase.
 */
import { useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getISOYear } from '../utils/overtimeUtils';
import { dateKey, getWeekNumber } from '../utils/dateUtils';
import type { GridRow, DailyIndemnity } from '../pages/timesheet/types';
import {
  buildTimesheetPDF,
  optimizeImage,
  LOGO_URL,
  SIGN_URL,
  type PDFProjectRow,
  type PDFIndemnityStats,
  type PDFGDStats,
  type PDFHoursStats,
} from '../utils/timesheetPDFBuilder';

interface UseTimesheetPDFParams {
  rows: GridRow[];
  weekDays: Date[];
  holidays: string[];
  trainingDays: string[];
  indemnities: Record<string, DailyIndemnity>;
  currentWeekStart: Date;
  isDelegateMode: boolean;
  targetEmployeeName: string;
  userName: string | undefined;
  isBE35h: boolean;
  customWeeklyHours: number | null;
  overrideWeeklyHours: number | null;
  calculateTotal: () => number;
  calculateDailyWinningZone: (dStr: string) => string | number | null;
  getGDProjectForDay: (dStr: string) => { id: string; code: string; name: string; gd_amount?: number } | null;
  saveToDatabase: (status: 'draft' | 'pending', signatureData?: string | null, pdfUrl?: string | null) => Promise<void>;
  fetchWeekData: () => Promise<void>;
  setSaving: (v: boolean) => void;
  setShowSignatureModal: (v: boolean) => void;
  onSuccess: () => void;
  navigate: (path: string) => void;
  sigCanvas: React.RefObject<any>;
  toast: { success: (msg: string) => void; error: (msg: string) => void; warning: (msg: string) => void; info: (msg: string) => void };
}

export function useTimesheetPDF(params: UseTimesheetPDFParams) {
  const {
    rows, weekDays, holidays, trainingDays, indemnities,
    currentWeekStart, isDelegateMode, targetEmployeeName, userName,
    isBE35h, customWeeklyHours, overrideWeeklyHours,
    calculateTotal, calculateDailyWinningZone, getGDProjectForDay,
    saveToDatabase, fetchWeekData,
    setSaving, setShowSignatureModal, onSuccess, navigate, sigCanvas,
    toast,
  } = params;

  // Guard anti-double-clic sur "Signer". Indispensable car handleFinalSign
  // génère un PDF (~1-2s CPU) puis upload Storage (~1-3s réseau) AVANT
  // d'appeler saveToDatabase. Le guard savingRef interne à useTimesheetActions
  // est donc trop bas dans la pile pour empêcher deux générations PDF
  // concurrentes (qui produiraient deux uploads + deux saves en course).
  const signingRef = useRef(false);

  const handleFinalSign = useCallback(async () => {
    if (signingRef.current) return;

    if (sigCanvas.current?.isEmpty()) {
      toast.warning("Veuillez signer avant de valider.");
      return;
    }

    // 1. Capture signature employé
    const originalCanvas = sigCanvas.current?.getCanvas();
    let employeeSignatureData = '';
    if (originalCanvas) {
      const tempCanvas = document.createElement('canvas');
      const ratio = originalCanvas.width / originalCanvas.height;
      tempCanvas.width = 300;
      tempCanvas.height = 300 / ratio;
      const ctx = tempCanvas.getContext('2d');
      ctx?.drawImage(originalCanvas, 0, 0, tempCanvas.width, tempCanvas.height);
      employeeSignatureData = tempCanvas.toDataURL('image/png');
    }
    if (!employeeSignatureData) { toast.error("Erreur signature"); return; }

    signingRef.current = true;
    setSaving(true);

    try {
      // 2. Charger images (best-effort)
      let logoData: string | null = null;
      let directionSignData: string | null = null;
      try {
        const [logo, direction] = await Promise.all([
          optimizeImage(LOGO_URL, 400),
          optimizeImage(SIGN_URL, 300)
        ]);
        logoData = logo;
        directionSignData = direction;
      } catch (err) {
        console.warn("Images non trouvées", err);
      }

      const empName = isDelegateMode ? targetEmployeeName : userName;
      const weekNum = getWeekNumber(currentWeekStart);
      const year = getISOYear(currentWeekStart);

      // 3. Préparer les données normalisées pour le builder
      const projectRows: PDFProjectRow[] = rows.map(row => ({
        projectName: row.projectName,
        dayHours: { ...row.hours },
      }));

      // Agréger les stats depuis le state local
      let totalMeals = 0, totalTransport = 0, totalTrajet = 0;
      const zonesCount: Record<string, number> = {};
      let totalNormal = trainingDays.length * 7;
      let totalSaturday = 0, totalSunday = 0;
      let totalGDDays = 0, totalGDAmount = 0, totalGDTravelHours = 0;
      const gdProjectsCountedForAmplitude = new Set<string>();

      rows.forEach(r => {
        Object.entries(r.hours).forEach(([ds, h]) => {
          const dayDate = new Date(ds);
          const dayOfWeek = dayDate.getDay();
          if (dayOfWeek === 0) totalSunday += h;
          else if (dayOfWeek === 6) totalSaturday += h;
          else totalNormal += h;
        });
      });

      weekDays.forEach(day => {
        const dKey = dateKey(day);
        if (trainingDays.includes(dKey)) return;
        const ind = indemnities[dKey];
        if (ind) {
          if (ind.meals && !ind.is_grand_deplacement) totalMeals += ind.meals;
          if (ind.transport && !ind.is_grand_deplacement) totalTransport += ind.transport;
          if (ind.trajet && !ind.is_grand_deplacement) totalTrajet += ind.trajet;
          if (ind.is_grand_deplacement) {
            totalGDDays++;
            if (ind.gd_amount) totalGDAmount += ind.gd_amount;
            const gdProject = getGDProjectForDay(dKey);
            if (gdProject && ind.gd_travel_hours && !gdProjectsCountedForAmplitude.has(gdProject.id)) {
              totalGDTravelHours += ind.gd_travel_hours;
              gdProjectsCountedForAmplitude.add(gdProject.id);
            }
          }
        }
        const winningZone = calculateDailyWinningZone(dKey);
        if (winningZone) {
          const zStr = winningZone.toString();
          zonesCount[zStr] = (zonesCount[zStr] || 0) + 1;
        }
      });

      const gdProject = getGDProjectForDay(weekDays[0] ? dateKey(weekDays[0]) : '');
      const gdDailyRate = gdProject?.gd_amount || (totalGDDays > 0 ? totalGDAmount / totalGDDays : 0);

      const indemnityStats: PDFIndemnityStats = { totalMeals, totalTransport, totalTrajet, zonesCount };
      const gdStats: PDFGDStats = {
        totalGDDays, totalGDAmount, totalGDTravelHours,
        gdProjectName: gdProject ? `${gdProject.code} - ${gdProject.name}` : 'Projet GD',
        gdDailyRate,
      };
      const hoursStats: PDFHoursStats = { totalNormal, totalSaturday, totalSunday };

      // 4. Construire le PDF
      const doc = buildTimesheetPDF({
        projectRows, weekDays, holidays, trainingDays,
        indemnityStats, gdStats, hoursStats,
        employeeName: empName || 'Inconnu',
        weekNumber: weekNum, year,
        dateLabel: `Généré le : ${new Date().toLocaleDateString('fr-FR')}`,
        isBE35h, customWeeklyHours, overrideWeeklyHours,
        employeeSignature: employeeSignatureData,
        logoData, directionSignData,
      });

      // 5. Upload PDF
      const pdfBlob = doc.output('blob');
      const safeName = empName?.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fileName = `${year}_S${weekNum}_${safeName}_${Date.now()}.pdf`;

      let publicPdfUrl = null;
      try {
        const { error: uploadError } = await supabase.storage
          .from('timesheets')
          .upload(fileName, pdfBlob, { contentType: 'application/pdf', upsert: true });
        if (uploadError) {
          console.error("Erreur Upload Storage:", uploadError);
          toast.warning("Attention : Le PDF n'a pas pu être sauvegardé. La feuille sera quand même transmise.");
        } else {
          const { data: urlData } = supabase.storage.from('timesheets').getPublicUrl(fileName);
          publicPdfUrl = urlData.publicUrl;
        }
      } catch (storageErr) {
        console.error("Crash Storage:", storageErr);
      }

      // 6. Save + finalize
      await saveToDatabase('pending', employeeSignatureData, publicPdfUrl);
      setShowSignatureModal(false);
      setTimeout(() => {
        fetchWeekData();
        onSuccess();
      }, 500);

    } catch (error) {
      console.error(error);
      toast.error("Erreur critique lors de la validation : " + (error instanceof Error ? error.message : "Erreur inconnue"));
    } finally {
      signingRef.current = false;
      setSaving(false);
    }
  }, [
    rows, weekDays, holidays, trainingDays, indemnities,
    currentWeekStart, isDelegateMode, targetEmployeeName, userName,
    isBE35h, customWeeklyHours, overrideWeeklyHours,
    calculateTotal, calculateDailyWinningZone, getGDProjectForDay,
    saveToDatabase, fetchWeekData, setSaving, setShowSignatureModal, onSuccess, navigate, sigCanvas, toast,
  ]);

  return { handleFinalSign };
}
